<script setup lang="ts">
import type { AppButtonState } from '@libs/ui';
import { AppButton, AppFlicker } from '@libs/ui';

import { useGlobalCurrencyStore } from '../../core/stores/globalCurrencyStore';
import { useItineraryStore, usePaxStore } from '../../core/stores';
import { useTripSummaryStore } from '../../pages/tripOverview/stores';

const globalCurrencyStore = useGlobalCurrencyStore();
const paxStore = usePaxStore();
const tripSummaryStore = useTripSummaryStore();
const itineraryStore = useItineraryStore();

interface PriceSummaryFooterMobileProps {
  buttonState?: AppButtonState;
  buttonText: string;
}
const props = withDefaults(defineProps<PriceSummaryFooterMobileProps>(), {
  buttonState: 'enabled',
});

interface PriceSummaryFooterMobileEmits {
  (eventName: 'button-clicked'): void;
}

const emits = defineEmits<PriceSummaryFooterMobileEmits>();
</script>

<template>
  <div class="price-summary-footer-mobile">
    <div class="price-summary-footer-mobile-wrapper">
      <div class="total-price">
        <div v-if="tripSummaryStore.tripGrossPrice" class="old-amount">
          <AppFlicker
            v-if="itineraryStore.isSearchingItineraries"
            width="spacing-40"
            height="font-size-m"
          />
          <span v-else>
            {{
              globalCurrencyStore.formatMoney(
                tripSummaryStore.tripTotalPrice +
                  tripSummaryStore.tripTotalAddonsPrice
              )
            }}
          </span>
        </div>
        <div class="amount">
          <AppFlicker
            v-if="itineraryStore.isSearchingItineraries"
            width="spacing-64"
            height="font-size-h5"
            :margin="{ ySpacing: 'spacing-2' }"
          />
          <template v-else>
            <span v-if="tripSummaryStore.tripGrossPrice" class="discount">
              {{
                globalCurrencyStore.formatMoney(
                  tripSummaryStore.tripGrossPrice +
                    tripSummaryStore.tripTotalAddonsPrice
                )
              }}
            </span>

            <span v-else>
              {{
                globalCurrencyStore.formatMoney(
                  tripSummaryStore.tripTotalPrice +
                    tripSummaryStore.tripTotalAddonsPrice
                )
              }}
            </span>
          </template>
        </div>
        <AppFlicker
          v-if="itineraryStore.isSearchingItineraries"
          width="spacing-40"
          height="font-size-h5"
        />
        <div
          v-else
          class="text"
          :class="{ gap: !tripSummaryStore.tripGrossPrice }"
        >
          {{ paxStore.numberOfTravelers }}
          {{
            $t(
              'planner.pax.travelers',
              paxStore.numberOfTravelers
            ).toLocaleLowerCase()
          }}
        </div>
      </div>

      <div>
        <AppButton
          type="important"
          size="large"
          :state="
            itineraryStore.isSearchingItineraries
              ? 'loadingDots'
              : props.buttonState
          "
          @click="emits('button-clicked')"
        >
          {{ props.buttonText }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-summary-footer-mobile {
  --side-margin: var(--spacing-12);
  width: calc(100vw - (var(--side-margin) * 2));
  margin: var(--spacing-16) var(--side-margin);
  .price-summary-footer-mobile-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-12);
    background-color: var(--color-white);
    box-shadow: var(--shadow-elevation-30);
    border: 1px solid var(--color-neutral-30);
    border-radius: var(--border-radius-12);

    .total-price {
      color: var(--color-neutral-70);
      display: flex;
      flex-direction: column;
      .text {
        @include font-label-s-light;
        &.gap {
          margin-top: var(--spacing-4);
        }
      }

      .amount {
        @include font-heading-5;
        .discount {
          color: var(--color-error-dark);
        }
      }

      .old-amount {
        @include font-label-m-bold;
        text-decoration: line-through;
      }
    }
  }
}
</style>
